import { HTTPError } from "ky";

import type { TGalaxyError } from "@up/data";

const DEFAULT_MESSAGE = "Something went wrong. Please try again.";

type ErrorResponseBody = {
  error?: {
    message?: string;
    details?: { errors?: TGalaxyError[] };
  };
};

export type CheckoutError =
  | { type: "validation"; message: string; validationErrors: TGalaxyError[] }
  | { type: "already_completed"; message: string }
  | { type: "http_error"; message: string; status: number }
  | { type: "network_error"; message: string }
  | { type: "unknown_error"; message: string; cause?: unknown };

export function parseCheckoutError(
  err: unknown,
  fallbackMessage = DEFAULT_MESSAGE,
): CheckoutError {
  if (!(err instanceof HTTPError)) {
    if (err instanceof TypeError && err.message === "Failed to fetch") {
      return { type: "network_error", message: fallbackMessage };
    }
    return { type: "unknown_error", message: fallbackMessage, cause: err };
  }

  const { status } = err.response;

  if (status === 409) {
    return {
      type: "already_completed",
      message: "Checkout has already been completed",
    };
  }

  const body = err.data as ErrorResponseBody | undefined;
  const message = body?.error?.message ?? fallbackMessage;
  const validationErrors = body?.error?.details?.errors;

  if (status === 400 && validationErrors?.length) {
    return { type: "validation", message, validationErrors };
  }

  return { type: "http_error", message, status };
}
